import { create } from 'zustand'
import type { AppelAgent } from '@/lib/voix/types'

/**
 * Store Zustand minimal — UI state de l'appel en cours (Twilio ou Telnyx).
 *
 * Périmètre strict : juste ce qui est *partagé* entre composants
 * (modale globale, mini-player, profil contact, etc.).
 *
 * Hors périmètre :
 * - Lifecycle Device → useTwilioDevice() hook
 * - Listeners 'incoming' → useIncomingCall() hook
 * - Actions (accept, mute, hold) → useCallActions() hook
 * - API calls (hold/transfer) → src/api/voice.ts mutations
 */

export type CallDirection = 'inbound' | 'outbound'

/**
 * Announced (warm) transfer: the agent talks to a colleague while the caller
 * waits on hold. The call then stays in that conference for its whole life
 * (hold, another transfer), even after the announcement is cancelled.
 */
export interface Annonce {
  conference: string
  label: string
  /** 'sonne': the colleague is ringing; 'en_ligne': they answered;
   *  null: no announcement under way, the call is simply in the conference. */
  etape: 'sonne' | 'en_ligne' | null
}

/** A person added to the call: neither the caller nor this agent. */
export interface Participant {
  /** The carrier leg: « Retirer » names it. */
  jambe: string
  label: string
  /** A number outside the team (2 per call at most). */
  externe: boolean
  /** They answered; otherwise they are ringing. */
  en_ligne: boolean
}

interface CallStoreState {
  // Appel en cours
  currentCall: AppelAgent | null
  callDirection: CallDirection
  isCallConnected: boolean
  isCallMuted: boolean
  isInternalCall: boolean
  callDuration: number

  // Modale globale d'appel entrant (PC)
  showGlobalIncomingCall: boolean
  callerContactName: string
  callerPhoneNumber: string

  // Hold
  isOnHold: boolean
  isHoldLoading: boolean

  // Transfer modal
  showTransferModal: boolean
  isTransferring: boolean

  // Announced transfer
  annonce: Annonce | null
  // The server is calling this phone back (announcement started): the old leg
  // ending must not close the call screen, and the callback is answered alone.
  retourAttendu: boolean

  // Call with several people: who was added, ringing or on the line.
  participants: Participant[]
  // « Ajouter » sent: the person is called once this phone is back in the conference.
  ajoutEnCours: string | null
  // The transfer modal opens to transfer, or to add someone to the call.
  transferMode: 'transfer' | 'add'

  // Keypad
  showGlobalKeypad: boolean

  // Permission micro
  showMicPermissionModal: boolean

  // Signal qu'une UI inline d'appel (InCallPanel sous timeline) est visible.
  // Le widget flottant s'auto-cache si true pour éviter le doublon.
  hasInlineCallUI: boolean

  // Setters
  setCurrentCall: (call: AppelAgent | null) => void
  setCallDirection: (dir: CallDirection) => void
  setIsCallConnected: (connected: boolean) => void
  setIsCallMuted: (muted: boolean) => void
  setIsInternalCall: (internal: boolean) => void
  setCallDuration: (duration: number | ((prev: number) => number)) => void
  setShowGlobalIncomingCall: (show: boolean) => void
  setCallerContactName: (name: string) => void
  setCallerPhoneNumber: (phone: string) => void
  setIsOnHold: (hold: boolean) => void
  setIsHoldLoading: (loading: boolean) => void
  setShowTransferModal: (show: boolean) => void
  setIsTransferring: (transferring: boolean) => void
  toggleGlobalKeypad: () => void
  setShowMicPermissionModal: (show: boolean) => void
  setHasInlineCallUI: (has: boolean) => void

  // Reset complet (fin d'appel, cleanup)
  reset: () => void
}

const initialState = {
  currentCall: null,
  callDirection: 'inbound' as CallDirection,
  isCallConnected: false,
  isCallMuted: false,
  isInternalCall: false,
  callDuration: 0,
  showGlobalIncomingCall: false,
  callerContactName: '',
  callerPhoneNumber: '',
  isOnHold: false,
  isHoldLoading: false,
  showTransferModal: false,
  isTransferring: false,
  annonce: null as Annonce | null,
  retourAttendu: false,
  participants: [] as Participant[],
  ajoutEnCours: null as string | null,
  transferMode: 'transfer' as 'transfer' | 'add',
  showGlobalKeypad: false,
  showMicPermissionModal: false,
  hasInlineCallUI: false,
}

export const useCallStore = create<CallStoreState>((set) => ({
  ...initialState,

  setCurrentCall: (call) => set({ currentCall: call }),
  setCallDirection: (dir) => set({ callDirection: dir }),
  setIsCallConnected: (connected) => set({ isCallConnected: connected }),
  setIsCallMuted: (muted) => set({ isCallMuted: muted }),
  setIsInternalCall: (internal) => set({ isInternalCall: internal }),
  setCallDuration: (duration) =>
    set((state) => ({
      callDuration:
        typeof duration === 'function' ? duration(state.callDuration) : duration,
    })),
  setShowGlobalIncomingCall: (show) => set({ showGlobalIncomingCall: show }),
  setCallerContactName: (name) => set({ callerContactName: name }),
  setCallerPhoneNumber: (phone) => set({ callerPhoneNumber: phone }),
  setIsOnHold: (hold) => set({ isOnHold: hold }),
  setIsHoldLoading: (loading) => set({ isHoldLoading: loading }),
  setShowTransferModal: (show) => set({ showTransferModal: show }),
  setIsTransferring: (transferring) => set({ isTransferring: transferring }),
  toggleGlobalKeypad: () => set((s) => ({ showGlobalKeypad: !s.showGlobalKeypad })),
  setShowMicPermissionModal: (show) => set({ showMicPermissionModal: show }),
  setHasInlineCallUI: (has) => set({ hasInlineCallUI: has }),

  reset: () => set(initialState),
}))

/**
 * True while the call is on a server-side hold (Twilio: the customer is moved
 * to a conference and the agent's leg drops on purpose), or while the server
 * is calling this phone back after starting an announced transfer (Twilio and
 * Telnyx). Its disconnect events must not reset the call screen. A native hold
 * (Telnyx) keeps the same call: a disconnect then really means the other party
 * hung up.
 */
export function attenteParLeServeur(): boolean {
  const { isOnHold, currentCall, retourAttendu } = useCallStore.getState()
  return (isOnHold && !currentCall?.attenteNative) || retourAttendu
}
